import { memo, useState, useEffect } from "react";
import type { ActivePosition } from "@shared/types";
import type { PositionAIDecisionsResponse } from "@app/services/tradingSystemApi";
import "./expandedAIAnalysis.css";

interface ExpandedAIAnalysisProps {
  position: ActivePosition;
  data: PositionAIDecisionsResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  onClose: () => void;
}

/**
 * Expanded AI Analysis Component
 * 
 * Displays detailed AI decision analysis including:
 * - Current decision with full details
 * - Technical indicators analysis
 * - Decision history timeline
 * - Automated actions taken
 */
export const ExpandedAIAnalysis = memo(function ExpandedAIAnalysis({
  position,
  data,
  isLoading,
  error,
}: ExpandedAIAnalysisProps): JSX.Element {
  if (isLoading) {
    return (
      <tr>
        <td colSpan={9} className="expanded-ai-analysis-cell">
          <div className="expanded-ai-analysis">
            <div className="expanded-ai-analysis__loading">Loading AI analysis...</div>
          </div>
        </td>
      </tr>
    );
  }

  if (error || !data) {
    return (
      <tr>
        <td colSpan={9} className="expanded-ai-analysis-cell">
          <div className="expanded-ai-analysis">
            <div className="expanded-ai-analysis__error">
              Unable to load AI analysis. Please try again later.
            </div>
          </div>
        </td>
      </tr>
    );
  }

  const formatTimestamp = (timestamp: string): string => {
    try {
      let date: Date;
      if (typeof timestamp === "string") {
        if (timestamp.endsWith("Z") || timestamp.includes("+") || timestamp.includes("-", 10)) {
          date = new Date(timestamp);
        } else {
          date = new Date(timestamp + "Z");
        }
      } else {
        date = new Date(timestamp);
      }
      if (Number.isNaN(date.getTime())) {
        return timestamp;
      }
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formatter = new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: userTimeZone,
      });
      return formatter.format(date);
    } catch (error) {
      return timestamp;
    }
  };

  const getActionIcon = (action: string): string => {
    const upperAction = action.toUpperCase();
    if (upperAction === "HOLD") return "🟢";
    if (upperAction === "EXIT" || upperAction === "PARTIAL_EXIT") return "🔴";
    if (upperAction.includes("ADJUST")) return "🟡";
    return "🤖";
  };

  return (
    <tr>
      <td colSpan={9} className="expanded-ai-analysis-cell">
        <div className="expanded-ai-analysis">
      <div className="expanded-ai-analysis__header">
        <h3 className="expanded-ai-analysis__title">🤖 AI Decision Engine - Live Analysis</h3>
        <div className="expanded-ai-analysis__subtitle">
          Position: {position.symbol}
          {data.current_decision?.timestamp && (
            <span className="expanded-ai-analysis__last-update">
              {" "}
              | Latest decision: {formatTimestamp(data.current_decision.timestamp)}
            </span>
          )}
        </div>
      </div>

      {data.current_decision && (
        <div className="expanded-ai-analysis__section">
          <h4 className="expanded-ai-analysis__section-title">📊 Current Decision</h4>
          <div className="expanded-ai-analysis__decision">
            <div className="expanded-ai-analysis__decision-header">
              <span className="expanded-ai-analysis__decision-icon">
                {getActionIcon(data.current_decision.action)}
              </span>
              <span className="expanded-ai-analysis__decision-action">
                {data.current_decision.action.toUpperCase()}
              </span>
              {data.current_decision.confidence !== null && (
                <span className="expanded-ai-analysis__decision-confidence">
                  {Math.round(data.current_decision.confidence)}% Confidence
                </span>
              )}
            </div>
            <div className="expanded-ai-analysis__decision-meta">
              <span>Updated: {formatTimestamp(data.current_decision.timestamp)}</span>
              <span>Decision Source: AI Strategic Decision Engine</span>
            </div>
            {data.current_decision.reason && (
              <div className="expanded-ai-analysis__decision-reason">
                {data.current_decision.reason}
              </div>
            )}
          </div>
        </div>
      )}

      {data.technical_indicators && (
        <div className="expanded-ai-analysis__section">
          <h4 className="expanded-ai-analysis__section-title">🔍 Technical Indicators Analysis</h4>
          <div className="expanded-ai-analysis__indicators">
            {data.technical_indicators.rsi !== undefined && data.technical_indicators.rsi !== null && (
              <div className="expanded-ai-analysis__indicator">
                <span className="expanded-ai-analysis__indicator-label">📈 RSI:</span>
                <span className="expanded-ai-analysis__indicator-value">
                  {data.technical_indicators.rsi.toFixed(1)}
                </span>
                <span className="expanded-ai-analysis__indicator-status">
                  {data.technical_indicators.rsi > 70
                    ? "🔴 Overbought"
                    : data.technical_indicators.rsi < 30
                    ? "🟢 Oversold"
                    : "🟡 Neutral"}
                </span>
              </div>
            )}

            {data.technical_indicators.macd && (
              <div className="expanded-ai-analysis__indicator">
                <span className="expanded-ai-analysis__indicator-label">📊 MACD:</span>
                <span className="expanded-ai-analysis__indicator-value">
                  {typeof data.technical_indicators.macd === "object"
                    ? data.technical_indicators.macd.signal || "N/A"
                    : String(data.technical_indicators.macd)}
                </span>
              </div>
            )}

            {data.technical_indicators.ema && (
              <div className="expanded-ai-analysis__indicator">
                <span className="expanded-ai-analysis__indicator-label">📉 EMA:</span>
                <span className="expanded-ai-analysis__indicator-value">
                  {typeof data.technical_indicators.ema === "object"
                    ? "Aligned"
                    : String(data.technical_indicators.ema)}
                </span>
              </div>
            )}

            {data.technical_indicators.technical_score !== undefined && (
              <div className="expanded-ai-analysis__indicator">
                <span className="expanded-ai-analysis__indicator-label">Overall Technical Score:</span>
                <div className="expanded-ai-analysis__score-bar">
                  <div
                    className="expanded-ai-analysis__score-bar-fill"
                    style={{
                      width: `${(data.technical_indicators.technical_score || 0) * 100}%`,
                    }}
                  />
                </div>
                <span className="expanded-ai-analysis__indicator-value">
                  {(data.technical_indicators.technical_score || 0) * 100}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="expanded-ai-analysis__section">
        <h4 className="expanded-ai-analysis__section-title">
          🎯 AI Decision History
        </h4>
        <div className="expanded-ai-analysis__history-split">
          <div className="expanded-ai-analysis__history-column">
            <h5 className="expanded-ai-analysis__history-column-title">
              📊 AI Decisions {data.decision_history && data.decision_history.length > 0 ? `(Last ${data.decision_history.length})` : "(No decisions yet)"}
            </h5>
            {data.decision_history && data.decision_history.length > 0 ? (
              <div className="expanded-ai-analysis__history">
                {data.decision_history.slice(0, 10).map((decision) => (
                  <div key={decision.id} className="expanded-ai-analysis__history-item">
                    <div className="expanded-ai-analysis__history-header">
                      <span className="expanded-ai-analysis__history-time">
                        🕐 {formatTimestamp(decision.timestamp)}
                      </span>
                      <span className="expanded-ai-analysis__history-icon">
                        {getActionIcon(decision.action)}
                      </span>
                      <span className="expanded-ai-analysis__history-action">
                        {decision.action.toUpperCase()}
                      </span>
                      {decision.confidence !== null && (
                        <span className="expanded-ai-analysis__history-confidence">
                          ({Math.round(decision.confidence)}%)
                        </span>
                      )}
                    </div>
                    {decision.reason && (
                      <div className="expanded-ai-analysis__history-reason">{decision.reason}</div>
                    )}
                    {decision.price_before && (
                      <div className="expanded-ai-analysis__history-price">
                        Price: {decision.price_before.toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="expanded-ai-analysis__history-empty">
                No AI decisions recorded yet for this position.
              </div>
            )}
          </div>
          <div className="expanded-ai-analysis__history-column">
            <h5 className="expanded-ai-analysis__history-column-title">
              💰 Partial Exits Executed {data.partial_exits && data.partial_exits.length > 0 ? `(${data.partial_exits.length})` : "(No exits yet)"}
            </h5>
            {data.partial_exits && data.partial_exits.length > 0 ? (
              <div className="expanded-ai-analysis__history">
                {data.partial_exits.map((exit, idx) => (
                  <div key={idx} className="expanded-ai-analysis__history-item expanded-ai-analysis__partial-exit-item">
                    <div className="expanded-ai-analysis__history-header">
                      <span className="expanded-ai-analysis__history-time">
                        🕐 {formatTimestamp(exit.timestamp)}
                      </span>
                      <span className="expanded-ai-analysis__partial-exit-tp">
                        🎯 {exit.tp_level}
                      </span>
                    </div>
                    <div className="expanded-ai-analysis__partial-exit-details">
                      <span className="expanded-ai-analysis__partial-exit-percentage">
                        Sold {exit.exit_percentage != null ? exit.exit_percentage.toFixed(0) : "N/A"}%
                      </span>
                      <span className="expanded-ai-analysis__partial-exit-price">
                        at {exit.exit_price != null ? exit.exit_price.toFixed(8) : "N/A"}
                      </span>
                      <span className="expanded-ai-analysis__partial-exit-pnl">
                        Profit: {exit.pnl_percentage != null 
                          ? `${exit.pnl_percentage > 0 ? "+" : ""}${exit.pnl_percentage.toFixed(2)}%`
                          : "N/A"}
                      </span>
                    </div>
                    {exit.reason && (
                      <div className="expanded-ai-analysis__history-reason">{exit.reason}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="expanded-ai-analysis__history-empty">
                No partial exits executed yet for this position.
              </div>
            )}
          </div>
        </div>
      </div>
        </div>
      </td>
    </tr>
  );
});

